import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { IoCheckmark, IoChevronDown } from 'react-icons/io5';

import {
  CheckIcon,
  ChevronIcon,
  Dropdown,
  EmptyState,
  Label,
  OptionButton,
  OptionLabel,
  SelectControl,
  TriggerButton,
  TriggerValue,
  Wrapper,
} from './styled';

export interface ISelect {
  label?: string;
  value: string;
  options: { value: string; label: string }[];
  // eslint-disable-next-line no-unused-vars
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Toolbar layout: no label, matches SearchInput height. */
  compact?: boolean;
}

const createChangeEvent = (value: string): React.ChangeEvent<HTMLSelectElement> =>
  ({
    currentTarget: { value },
    target: { value },
  }) as React.ChangeEvent<HTMLSelectElement>;

export const Select: React.FC<ISelect> = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = 'Select an option...',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const labelId = useId();

  const selectedIndex = useMemo(
    () => options.findIndex((option) => option.value === value),
    [options, value],
  );
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const displayLabel = selectedOption?.label ?? placeholder;
  const showPlaceholder = !selectedOption;

  const close = useCallback(() => {
    setOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const selectValue = useCallback(
    (nextValue: string) => {
      onChange(createChangeEvent(nextValue));
      close();
    },
    [close, onChange],
  );

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        close();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [close, open]);

  useEffect(() => {
    if (open) {
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [open, selectedIndex]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((current) => Math.min(current + 1, options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((current) => Math.max(current - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setHighlightedIndex(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          selectValue(options[highlightedIndex].value);
        }
        break;
      default:
        break;
    }
  };

  return (
    <Wrapper ref={wrapperRef} $compact={compact}>
      {label ? <Label id={labelId}>{label}</Label> : null}
      <SelectControl $compact={compact}>
        <TriggerButton
          type="button"
          disabled={disabled}
          $open={open}
          $disabled={disabled}
          $compact={compact}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-labelledby={label ? labelId : undefined}
          onClick={() => {
            if (disabled) return;
            setOpen((current) => !current);
          }}
          onKeyDown={handleKeyDown}
        >
          <TriggerValue $placeholder={showPlaceholder} title={displayLabel}>
            {displayLabel}
          </TriggerValue>
          <ChevronIcon $open={open} aria-hidden>
            <IoChevronDown size={16} />
          </ChevronIcon>
        </TriggerButton>

        {open ? (
          <Dropdown
            id={listId}
            role="listbox"
            aria-label={label || 'Select option'}
            $compact={compact}
          >
            {options.length ? (
              options.map((option, index) => {
                const isSelected = option.value === value;
                const isHighlighted = index === highlightedIndex;

                return (
                  <li key={option.value} role="presentation">
                    <OptionButton
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      $selected={isSelected}
                      $highlighted={isHighlighted && !isSelected}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => selectValue(option.value)}
                    >
                      <OptionLabel title={option.label}>{option.label}</OptionLabel>
                      {isSelected ? (
                        <CheckIcon aria-hidden>
                          <IoCheckmark size={16} />
                        </CheckIcon>
                      ) : null}
                    </OptionButton>
                  </li>
                );
              })
            ) : (
              <EmptyState>No options available</EmptyState>
            )}
          </Dropdown>
        ) : null}
      </SelectControl>
    </Wrapper>
  );
};
